/**
 * Homie CAPI 橋接器 v3
 * 作用：將 COD 結帳頁的用戶行為事件，同時發給：
 *   1. 瀏覽器端 Meta Pixel (fbq) — 自動由 Shopify theme 載入
 *   2. 我們的後端 CAPI 服務 — 安全、不受廣告阻擋影響
 *
 * 兩邊使用相同的 event_id → Meta 自動去重，只計算一次
 * Access Token 完全不存在前端，只放在後端資料庫
 *
 * 🔧 v3 變更（2026-04-24）：
 *   - API_BASE 改為讀取全域變數 window.COD_API_BASE
 *   - 與 page.cod-checkout.liquid 共用同一處設定，未來只需在 liquid 改一處
 *
 * 🔧 v4 變更（Stage 2D-2, 2026-05-07）：
 *   - endpoint: /api/accounting/track-capi-event → /api/track-event（統一 pipeline）
 *   - body shape: event/eventId/eventSourceUrl/products/totalValue
 *     → event_name/event_id/page_url/product_titles+contents/value+currency
 *   - fireEvent 加 sid 參數（送 session_id 到 server,提升 EMQ）
 */

(function () {
  'use strict';

  // ── 設定 ──────────────────────────────────────────────
  // 優先序：window.COD_API_BASE (liquid 統一設定) > window.homieConfig (向後相容) > hardcoded fallback
  var API_BASE = window.COD_API_BASE
    || (window.homieConfig && window.homieConfig.apiBaseUrl)
    || 'https://neizhang.replit.app';
  // Stage 2D-2: 切換到統一 endpoint /api/track-event
  var CAPI_ENDPOINT = API_BASE + '/api/track-event';

  // ── 工具函數 ──────────────────────────────────────────

  function getCookie(name) {
    var match = document.cookie.match(new RegExp('(?:^|;\\s*)' + name + '=([^;]*)'));
    return match ? decodeURIComponent(match[1]) : '';
  }

  function getFbp() {
    return getCookie('_fbp');
  }

  function getFbc() {
    var fbc = getCookie('_fbc');
    if (!fbc) {
      var params = new URLSearchParams(window.location.search);
      var fbclid = params.get('fbclid');
      if (fbclid) {
        fbc = 'fb.1.' + Date.now() + '.' + fbclid;
        try { document.cookie = '_fbc=' + fbc + '; path=/; max-age=7776000'; } catch (e) {}
      }
    }
    return fbc;
  }

  function getOrCreateSessionId() {
    try {
      var sid = sessionStorage.getItem('_hmSid');
      if (!sid) {
        sid = Math.random().toString(36).slice(2) + Date.now().toString(36);
        sessionStorage.setItem('_hmSid', sid);
      }
      return sid;
    } catch (e) {
      return Math.random().toString(36).slice(2);
    }
  }

  function hasTracked(key) {
    try { return sessionStorage.getItem('_hmt_' + key) === '1'; } catch (e) { return false; }
  }

  function markTracked(key) {
    try { sessionStorage.setItem('_hmt_' + key, '1'); } catch (e) {}
  }

  // ── 事件發送 ──────────────────────────────────────────

  /**
   * 同時觸發：
   *   fbq('track', eventName, data, { eventID: eventId })   ← 瀏覽器 Pixel
   *   POST /api/track-event { event_name, event_id, ... }   ← 後端 CAPI（Stage 2D 統一 endpoint）
   *
   * @param {string} eventName  PageView/ViewContent/AddToCart/InitiateCheckout
   * @param {string} eventId    Meta 去重用,瀏覽器/伺服器兩邊必須相同
   * @param {object} cartData   { products: [{title, quantity, price?}], totalValue }
   * @param {string} sid        session_id(EMQ 提升用,Stage 2D-2 新增)
   */
  function fireEvent(eventName, eventId, cartData, sid) {
    var fbp = getFbp();
    var fbc = getFbc();
    var url = window.location.href;
    var products = cartData.products || [];
    var totalValue = cartData.totalValue || 0;

    // 1. 觸發瀏覽器端 Pixel（fbq 由 Shopify theme 全域載入）
    if (typeof fbq === 'function') {
      try {
        var fbqData = {
          currency: 'TWD',
          value: totalValue,
          content_type: 'product',
          contents: products.map(function (p) {
            return { id: String(p.variant_id || p.title), quantity: p.quantity };
          }),
        };
        if (products.length > 0) {
          fbqData.content_name = products.map(function (p) { return p.title; }).join(', ');
        }
        fbq('track', eventName, fbqData, { eventID: eventId });
      } catch (e) {
        console.warn('[HomieTrack] fbq error:', e);
      }
    }

    // 2. 打後端 CAPI（相同 eventId → Meta 去重）
    // Stage 2D-2: 改用 /api/track-event,body shape 改 event_name/event_id/page_url/...
    var body = {
      event_name: eventName,
      event_id: eventId,
      page_url: url,
      currency: 'TWD',
      value: totalValue,
      num_items: products.reduce(function (n, p) { return n + (p.quantity || 0); }, 0),
      product_titles: products.map(function (p) { return p.title; }),
      contents: products.map(function (p) {
        var item = { id: String(p.variant_id || p.title), quantity: p.quantity || 0 };
        if (typeof p.price === 'number') item.item_price = p.price;
        return item;
      }),
    };
    if (sid) body.session_id = sid;
    if (fbp) body.fbp = fbp;
    if (fbc) body.fbc = fbc;

    fetch(CAPI_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }).then(function (r) {
      return r.json();
    }).then(function (data) {
      console.log('[HomieTrack] CAPI ' + eventName + ':', data && data.ok ? '✅' : '❌', data);
    }).catch(function (e) {
      console.warn('[HomieTrack] CAPI error:', e.message);
    });
  }

  // ── 主流程（COD 結帳頁專用）───────────────────────────

  /**
   * (2026-06-24) 抽出 ViewContent + InitiateCheckout 觸發邏輯
   *   供 initTracking 直接呼叫（Liquid 有資料時）或 homie:cart-ready 回呼（Liquid 無資料時）
   *   hasTracked 去重確保即使兩邊都 call 也只會發一次
   */
  function fireVCAndIC(cartData, sid) {
    if (!cartData || !cartData.products || cartData.products.length === 0) return;

    // ViewContent（頁面載入，有商品）
    if (!hasTracked('vc')) {
      markTracked('vc');
      fireEvent('ViewContent', 'vc_' + sid, cartData, sid);
    }

    // InitiateCheckout — Stage 3-B (TD-23, 2026-05-11): 進頁 + 有商品就 fire
    if (!hasTracked('ic')) {
      markTracked('ic');
      fireEvent('InitiateCheckout', 'ic_' + sid, cartData, sid);
    }
  }

  function initTracking() {
    var cartData = window.homieCartData || { products: [], totalValue: 0 };
    var sid = getOrCreateSessionId();

    // 1. PageView（頁面載入時，只觸發一次，不依賴商品資料）
    if (!hasTracked('pv')) {
      markTracked('pv');
      fireEvent('PageView', 'pv_' + sid, { products: [], totalValue: 0 }, sid);
    }

    // 2. 嘗試用 Liquid server-side 注入的 cartData 觸發 VC + IC
    if (cartData.products && cartData.products.length > 0) {
      fireVCAndIC(cartData, sid);
    } else {
      // (2026-06-24) Liquid 資料為空（CDN 快取 / session 延遲）→
      //   監聽 page.cod-checkout.liquid loadCartAndRender() 成功後發出的 homie:cart-ready 事件
      //   將客戶端 /cart.js 取得的真實資料轉換後補報 VC + IC
      //   hasTracked 守門確保即使 Liquid 與 AJAX 都有資料也不會雙發
      document.addEventListener('homie:cart-ready', function (e) {
        try {
          var clientCart = e.detail;
          if (!clientCart || !clientCart.items || clientCart.items.length === 0) return;
          var formattedCartData = {
            products: clientCart.items.map(function (it) {
              return {
                variant_id: it.variant_id,
                title: it.product_title || it.title || '',
                quantity: it.quantity || 1,
                price: (it.final_price != null ? it.final_price : (it.price || 0)) / 100
              };
            }),
            totalValue: (clientCart.total_price || 0) / 100
          };
          fireVCAndIC(formattedCartData, sid);
        } catch (err) {
          try { console.warn('[HomieTrack] cart-ready handler error:', err); } catch (_) {}
        }
      });
    }
  }

  // ── 等頁面 DOM 準備好後啟動 ────────────────────────────

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initTracking);
  } else {
    initTracking();
  }

})();
